import apiClient from './config';
import { Client, PortalSyncResult } from "../../types/index";
import { EventoWithDetails } from '../../types/eventos';

export const fetchClientes = async (): Promise<Client[]> => {
    const response = await apiClient.get('/clients');
    return response.data;
};

export const fetchAllClients = async () => {
    const response = await apiClient.get('/clients/all');
    return response.data;
};

// Nueva función para buscar cliente por teléfono
export const fetchClientByTelefono = async (telefono: string) => {
    console.log('🔍 fetchClientByTelefono - Nueva versión cargada para:', telefono);
    try {
        const response = await apiClient.get(`/clients/buscar-telefono/${telefono}`);
        console.log('✅ Cliente encontrado:', response.data);
        return response.data;
    } catch (error: any) {
        // Si es 404, el cliente no existe - esto es una respuesta válida, no un error
        if (error.response?.status === 404) {
            console.log('📞 Cliente no existe (404) - retornando null, NO lanzando error');
            return null; // Retornamos null en lugar de lanzar error
        }
        // Para otros errores (500, network, etc.) sí lanzamos la excepción
        console.log('❌ Error real (no 404):', error);
        throw error;
    }
};

// Función para detalle completo
export const fetchClientDetails = async (clientId: string) => {
    const response = await apiClient.get(`/clients/${clientId}/details`);
    return response.data;
};

// Nueva función para clientes con plazas
export const fetchClientsWithPlazas = async (includeInactive = false) => {
    const response = await apiClient.get(`/clients/with-plazas${includeInactive ? '?activo=all' : ''}`);
    return response.data;
};

// Función para obtener eventos del cliente con límite opcional
export const fetchClientEvents = async (clientId: string, options: { limit?: number } = {}) => {
    const { limit = 5 } = options;
    const response = await apiClient.get(`/clients/${clientId}/events?limit=${limit}`);
    return response.data as EventoWithDetails[];
};

export const createClient = async (clientData: Partial<Client>): Promise<Client> => {
    const response = await apiClient.post('/clients', clientData);
    return response.data.client;
};

// B5 — devuelve también `portalSync`: si se ha cambiado el correo, dice si se
// pudo arrastrar a Clerk (la cuenta de acceso al portal). Antes solo se devolvía
// el cliente y el desfase quedaba invisible.
export const updateClient = async (
    id: string,
    clientData: Partial<Client>
): Promise<{ client: Client; portalSync?: PortalSyncResult }> => {
    const response = await apiClient.put(`/clients/${id}`, clientData);
    return { client: response.data.client, portalSync: response.data.portalSync };
};

export const deleteClient = async (id: string): Promise<void> => {
    await apiClient.delete(`/clients/${id}`);
};

export type PortalAccountStatus = 'active' | 'registered' | 'invited' | 'not_invited' | 'no_email' | 'unknown';

export interface ClientPortalStatus {
    status: PortalAccountStatus;
    email?: string;
    lastSignInAt?: number | null;
    invitedAt?: number | null;
}

export const fetchClientPortalStatus = async (id: string): Promise<ClientPortalStatus> => {
    const response = await apiClient.get(`/clients/${id}/portal-status`);
    return response.data;
};

export const fetchOpcionesAlta = async (): Promise<{ invitarPortal: boolean }> => {
    const response = await apiClient.get('/clients/opciones-alta');
    return response.data;
};

export interface OpcionesTarifa {
    tarifas: Array<{ _id: string; nombre: string }>;
    combinaciones: Array<{ _id: string; nombre: string }>;
    nivelesConTarifa: string[];
}

export const fetchOpcionesTarifa = async (): Promise<OpcionesTarifa> => {
    const response = await apiClient.get('/clients/opciones-tarifa');
    return response.data;
};

export const resendClientInvite = async (id: string): Promise<{ message: string }> => {
    const response = await apiClient.post(`/clients/${id}/resend-invite`);
    return response.data;
};

// --- Historial de actividad del cliente (G1) ---

export type ClientActivityType =
    | 'reservation'
    | 'cancellation'
    | 'recovery_earned'
    | 'recovery_used'
    // Una plaza recurrente ("todos los lunes a las 10:00").
    | 'enrollment_added'
    | 'enrollment_removed'
    // E3 — el centro metió o sacó al cliente de UNA clase con fecha, desde el
    // detalle del evento. Es otra cosa que una plaza, y se pinta distinto.
    | 'class_added'
    | 'class_removed'
    // El centro le retiró una recuperación pendiente.
    | 'recovery_revoked'
    // P6 — se pasó lista en una clase. Estas filas son una VISTA del feed, no la
    // fuente: caducan a los 60 días con el resto del historial. La asistencia
    // duradera vive en su propia colección.
    | 'attendance_present'
    | 'attendance_absent';

export interface ClientActivity {
    _id: string;
    clientId: string;
    type: ClientActivityType;
    eventId?: string | null;
    plazaId?: string | null;
    levelId?: { _id: string; nombre: string; color?: string } | string | null;
    // Todo opcional a propósito: las filas anteriores a cada cambio no traen los
    // campos nuevos, y una fila vieja tiene que seguir leyéndose.
    meta?: {
        fecha?: string;
        horaInicio?: string;
        dia?: string;
        levelName?: string;
        source?: 'client_cancel' | 'admin_cancel' | 'holiday' | 'enrollment_adjustment' | 'manual';
        // B9 — por qué una cancelación dio o no recuperación. `recoveryGranted`
        // ausente no es lo mismo que `false`: significa que la fila es anterior
        // al despliegue que empezó a guardarlo.
        recoveryGranted?: boolean;
        // Para `cancellation` es uno de los dos motivos cerrados de B9; para
        // `recovery_revoked` es el texto que escribió el operador.
        reason?: 'late' | 'outside_horizon' | (string & {});
        hoursUntilClass?: number;
        // E3 — quién hizo el cambio, para class_added / class_removed.
        actor?: string;
    };
    occurredAt: string;
    createdAt: string;
}

// --- Recuperaciones pendientes del cliente (M10) ---

type ClaseRef = { _id: string; fecha: string; horaInicio: string } | string | null;

export interface ClientRecovery {
    _id: string;
    levelId?: { _id: string; nombre: string; color?: string } | string | null;
    /** La clase que se canceló y dio derecho a la recuperación. */
    originalEventId?: ClaseRef;
    /** E4 — la clase en la que se gastó. Solo viene en `status: 'rescheduled'`. */
    rescheduledEventId?: ClaseRef;
    cancelledAt: string;
    rescheduleDeadline: string;
    /**
     * B11 — la recuperación de la que hereda el plazo. Presente cuando el
     * cliente entró a la clase cancelada gastando un derecho: entonces ésta
     * conserva el plazo de aquélla en vez de estrenar ventana, y sin decirlo la
     * fecha parece un error de cálculo.
     */
    deadlineInheritedFrom?: string | null;
    // 'rescheduled' significa GASTADA. No existe 'used'.
    status: 'pending' | 'rescheduled' | 'expired';
    source?: 'client_cancel' | 'admin_cancel' | 'holiday' | 'enrollment_adjustment' | 'manual';
    note?: string | null;
}

/**
 * E4 — recuento por estado. **No es un saldo histórico**: las recuperaciones se
 * barren 60 días después de su fecha límite, así que esto es lo que queda en la
 * ventana de retención. La interfaz tiene que decirlo.
 */
export interface ClientRecoverySummary {
    pending: number;
    rescheduled: number;
    expired: number;
}

// Niveles con plaza activa del cliente: los únicos en los que puede canjear una
// recuperación, y por tanto los únicos que se ofrecen al otorgarla a mano.
export interface ClientLevelOption {
    _id: string;
    nombre: string;
    color?: string;
}

export const fetchClientRecoveries = async (
    id: string,
): Promise<{
    count: number;
    /** Solo las pendientes, como siempre. */
    items: ClientRecovery[];
    availableLevels: ClientLevelOption[];
    // E4. Opcionales: una respuesta de un backend anterior no los trae.
    summary?: ClientRecoverySummary;
    history?: ClientRecovery[];
    historyTruncated?: boolean;
}> => {
    const response = await apiClient.get(`/clients/${id}/recoveries`);
    return response.data;
};

export const grantClientRecovery = async (
    id: string,
    payload: { levelId: string; note?: string },
): Promise<ClientRecovery> => {
    const response = await apiClient.post(`/clients/${id}/recoveries`, payload);
    return response.data;
};

// El motivo solo es obligatorio para las que el cliente se ganó al cancelar;
// el backend es quien decide y responde 400 MOTIVO_REQUERIDO si falta.
export const revokeClientRecovery = async (
    id: string,
    recoveryId: string,
    reason?: string,
): Promise<void> => {
    await apiClient.delete(`/clients/${id}/recoveries/${recoveryId}`, {
        data: { reason },
    });
};

export interface ClientActivityPage {
    items: ClientActivity[];
    total: number;
    page: number;
    limit: number;
    pages: number;
}

export const fetchClientActivity = async (
    id: string,
    page = 1,
    limit = 20,
): Promise<ClientActivityPage> => {
    const response = await apiClient.get(`/clients/${id}/activity`, { params: { page, limit } });
    return response.data;
};


// ── P6 — asistencia del cliente ──────────────────────────────────────────────

export interface ClientAttendanceRow {
    _id: string;
    eventId: string;
    status: 'present' | 'absent';
    markedBy: string | null;
    markedAt: string;
    levelName: string | null;
    fecha: string | null;
    horaInicio: string | null;
}

/**
 * Sale de la colección `attendances`, NO del feed de actividad: ese caduca a los
 * 60 días. `summary` cuenta solo lo marcado, así que presentes + ausentes no
 * cuadra con las clases del cliente — las que nadie pasó no son ausencias.
 */
export const fetchClientAttendance = async (
    id: string,
    page = 1,
): Promise<{
    items: ClientAttendanceRow[];
    summary: { present: number; absent: number };
    total: number;
    page: number;
    pages: number;
}> => {
    const response = await apiClient.get(`/clients/${id}/attendance`, { params: { page } });
    return response.data;
};
